<template>
  <section class="regulations">
    <h2 class="regulations__title">как попасть на чемпионат</h2>

    <div class="regulations__steps">
      <Card
        v-for="step in steps"
        :key="step.number"
        color="white"
        class="regulations__step"
      >
        <span class="regulations__step-number">{{ step.number }}</span>
        <!-- prettier-ignore -->
        <p class="regulations__step-text">{{ step.text }}<span class="regulations__accent">{{ step.accent }}</span></p>
      </Card>
    </div>

    <div class="regulations__universities">
      <p class="regulations__universities-head">
        <span class="regulations__universities-icon">
          <img :src="iconPromocode" alt="" width="32" height="32" />
        </span>
        <span class="regulations__universities-title">ВУЗы</span>
      </p>
      <p class="regulations__universities-text">
        Принять участие могут студенты из:
      </p>
      <div class="regulations__universities-list">
        <p
          v-for="university in universities"
          :key="university"
          class="regulations__university"
        >
          {{ university }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import Card from "@/components/Card.vue";
import iconPromocode from "@/assets/case-championship/icon-promocode.svg";

// Вторая часть карточки всегда начинается с новой строки — перенос задан
// вёрсткой, а не шириной карточки (см. &__accent).
const steps = [
  { number: 1, text: "Соберите команду", accent: "из 3-4 человек" },
  { number: 2, text: "Зарегистрируйтесь", accent: "до 14 октября" },
  { number: 3, text: "Решите кейс", accent: "до 5 ноября" },
  { number: 4, text: "Презентуйте решение", accent: "24 ноября" },
];

// Порядок вузов взят из десктопной ноды макета. В мобильной ноде он другой —
// приводим обе раскладки к одному списку.
// Союзы и предлоги приклеены к следующему слову неразрывным пробелом —
// так же, как в макете, где «и» уезжает в начало строки, а не висит в конце.
const universities = [
  "Московский институт электроники и математики им. А. Н. Тихонова (НИУ ВШЭ)",
  "Московский технический университет связи и информатики",
  "Поволжский государственный университет телекоммуникаций и информатики",
  "Санкт-Петербургский государственный университет телекоммуникаций имени профессора М. А. Бонч-Бруевича",
  "Сибирский государственный университет телекоммуникаций и информатики",
];
</script>

<style lang="scss" scoped>
.regulations {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__title {
    color: var(--colorBlack);
    font-size: var(--sizeHeading2);
    line-height: 26px;

    @include md {
      line-height: 30px;
    }
  }

  &__steps {
    display: flex;
    flex-direction: column;
    gap: 8px;

    @include md {
      flex-direction: row;
      align-items: stretch;
      // До 1024 четыре карточки в строку не помещаются: самое длинное слово
      // шире карточки, и блок растягивал страницу горизонтально.
      flex-wrap: wrap;
      gap: 16px;
    }
  }

  // Фон, радиус, внутренний отступ и тень карточки даёт примитив Card
  // в варианте white — они совпадают с макетом до значений.
  &__step {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 16px;
    overflow: clip;

    @include md {
      // По две в ряд: вчетвером карточки не помещаются до 1340 — см. above-steps.
      flex: 1 0 45%;
      min-width: 1px;
      gap: 24px;
    }

    @include above-steps {
      flex: 1 0 0;
    }
  }

  // В макете вместо «шаг 1» — чёрный кружок с номером шага.
  // align-self снимает растягивание: карточка — колоночный флекс, и без него
  // кружок разъехался бы на всю ширину.
  &__step-number {
    align-self: flex-start;
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    background: var(--colorBlack);
    border-radius: 999px;
    color: var(--colorWhite);
    font-family: var(--fontSecondary);
    font-weight: 800;
    font-size: 14px;
    line-height: 20px;
  }

  &__step-text {
    font-family: var(--fontSecondary);
    font-weight: 800;
    // 18px литералом: --sizeHeading4 на md даёт 20px, а в макете — 18px.
    font-size: 18px;
    line-height: 20px;
    text-transform: uppercase;
    // В макете это отдельный токен black/general — в дизайн-системе проекта
    // переменной для него нет, поэтому литерал.
    color: #1f2229;
  }

  // Акцент — вторая строка карточки. Блок, а не инлайн: перенос держится
  // на всех ширинах, а не появляется сам при сужении карточки.
  &__accent {
    display: block;
    font-weight: 500;
    // В макете акцент («3-4 человек», «до 14 октября») не разрывается:
    // дефис в «3-4» иначе становится точкой переноса.
    white-space: nowrap;
  }

  &__universities {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 8px;
    background: var(--colorPink);
    border-radius: var(--radiusOuter);
    overflow: clip;

    @include md {
      padding: 24px;
    }
  }

  &__universities-head {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__universities-icon {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.32);
    border-radius: 16px;
    overflow: clip;
  }

  &__universities-title {
    font-family: var(--fontSecondary);
    font-weight: 800;
    // См. комментарий у &__step-text.
    font-size: 18px;
    line-height: 20px;
    color: var(--colorWhite);
    text-transform: uppercase;
  }

  // Отступ от заголовка даёт gap контейнера &__universities.
  &__universities-text {
    color: var(--colorWhite);
    font-size: var(--sizeText);
    line-height: var(--lineHeightText);
  }

  &__universities-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;

    @include md {
      flex-direction: row;
      flex-wrap: wrap;
    }
  }

  &__university {
    display: flex;
    // В макете карточка задана как items-start — текст прижат к верху,
    // а не к низу карточки.
    align-items: flex-start;
    padding: 16px;
    background: var(--colorWhite);
    border-radius: 12px;
    color: var(--colorBlack);
    font-size: var(--sizeText);
    line-height: var(--lineHeightText);

    @include md {
      flex: 1 1 calc((100% - 16px) / 3);
      min-height: 112px;
      border-radius: 16px;
    }
  }
}
</style>
